import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Crown, Shield, Clock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Home() {
  const navigate = useNavigate();
  const [featuredWatches, setFeaturedWatches] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedWatches();
    fetchBrands();
  }, []);

  const fetchFeaturedWatches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/listings?limit=8`);
      setFeaturedWatches(response.data.listings);
    } catch (error) {
      console.error('Error fetching featured watches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setBrands(response.data.brands || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(price);
  };

  const displayBrands = brands.length > 0 ? brands : ['Rolex', 'Patek Philippe', 'Audemars Piguet', 'Omega', 'Cartier', 'Vacheron Constantin', 'Richard Mille'];

  return (
    <div className="min-h-screen">
      {/* Hero — full-bleed, Chrono24 mobile style */}
      <section className="relative overflow-hidden min-h-[85vh] sm:min-h-[60vh] lg:min-h-[72vh]" data-testid="hero-section">
        {/* Background image */}
        <img
          src="/hero-bg.webp"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-[45%_30%] sm:object-[70%_45%] md:object-[80%_45%] lg:object-[88%_42%]"
        />
        {/* Mobile: bottom gradient only (image breathes at top). Desktop: left gradient */}
        <div className="absolute inset-0 sm:hidden" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.75) 70%, rgba(0,0,0,0.92) 100%)' }} />
        <div className="absolute inset-0 hidden sm:block" style={{ background: 'linear-gradient(90deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.15) 65%, rgba(0,0,0,0.05) 100%)' }} />
        <div className="absolute inset-0 hidden sm:block" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 40%, transparent 70%, rgba(0,0,0,0.4) 100%)' }} />

        {/* Content — mobile: bottom-pinned compact. Desktop: left safe-zone */}
        <div className="relative z-10 h-full min-h-[85vh] sm:min-h-[60vh] lg:min-h-[72vh] flex items-end sm:items-end lg:items-center">
          <div className="w-full px-5 sm:px-6 lg:px-0 pb-8 sm:pb-14 lg:pb-0 lg:pt-0" style={{ paddingLeft: 'clamp(20px, 4vw, 64px)' }}>
            <div className="max-w-[1280px] mx-auto w-full">
              <div className="max-w-[90%] sm:max-w-none" style={{ maxWidth: 'clamp(320px, 40vw, 540px)' }}>
                <p className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] uppercase text-primary/90 mb-2 sm:mb-3" data-testid="hero-kicker">
                  Verified &amp; Authenticated
                </p>
                <h1 className="font-bold text-white mb-3 sm:mb-4 text-[32px] sm:text-[28px] md:text-[36px] lg:text-[48px] xl:text-[56px]" style={{ lineHeight: 1.0, letterSpacing: '-0.02em' }} data-testid="hero-headline">
                  The World's Most Trusted Watch Marketplace
                </h1>

                {/* Subtext — hidden on mobile for cleaner look */}
                <p className="hidden sm:block text-[13px] sm:text-sm text-white/60 mb-6 leading-relaxed" style={{ maxWidth: 'clamp(280px, 35vw, 420px)' }}>
                  Every timepiece authenticated. Every transaction secured. Buy and sell with confidence.
                </p>

                {/* Search — hidden on mobile, shown on desktop */}
                <div className="hidden sm:block mb-6" style={{ maxWidth: 'clamp(280px, 38vw, 480px)' }}>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search watches, brands, references..."
                      className="w-full h-10 sm:h-11 rounded-full border border-white/15 pl-4 pr-10 text-[13px] text-white placeholder:text-white/35 focus:outline-none focus:border-primary/50 transition-colors"
                      style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(6px)' }}
                      onKeyDown={(e) => { if (e.key === 'Enter' && e.target.value) navigate(`/catalog?search=${encodeURIComponent(e.target.value)}`); }}
                      data-testid="hero-search-input"
                    />
                    <ArrowRight className="absolute right-3.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
                  </div>
                </div>

                {/* CTAs — mobile: single primary button. Desktop: two buttons */}
                <div className="flex flex-wrap gap-2.5 mb-4 sm:mb-6">
                  <Button size="lg" onClick={() => navigate('/catalog')} data-testid="hero-browse-button"
                    className="h-12 sm:h-10 px-8 sm:px-5 text-sm sm:text-[13px] rounded-full font-semibold w-full sm:w-auto">
                    Browse Watches
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                  </Button>
                  <button onClick={() => navigate('/brands')} data-testid="hero-brands-button"
                    className="hidden sm:flex h-10 px-5 text-[13px] rounded-full font-medium border border-white/25 text-white/80 bg-transparent hover:bg-white/10 transition-colors items-center">
                    Explore Brands
                  </button>
                </div>

                {/* Trust row — hidden on mobile */}
                <div className="hidden sm:flex flex-wrap items-center gap-4 text-[11px] text-white/40" data-testid="hero-trust-bar">
                  <div className="flex items-center gap-1.5">
                    <Shield className="h-3 w-3 text-primary/70 flex-shrink-0" />
                    <span>Escrow Protection</span>
                  </div>
                  <span aria-hidden="true" className="text-white/15">&bull;</span>
                  <div className="flex items-center gap-1.5">
                    <Crown className="h-3 w-3 text-primary/70 flex-shrink-0" />
                    <span>Verified Listings</span>
                  </div>
                  <span aria-hidden="true" className="text-white/15">&bull;</span>
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3 w-3 text-primary/70 flex-shrink-0" />
                    <span>Insured Shipping</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Watches */}
      <section className="py-16 sm:py-20" data-testid="featured-watches-section">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px]">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl text-foreground tracking-tight font-semibold mb-2" data-testid="featured-title">
                Featured Timepieces
              </h2>
              <p className="text-muted-foreground">Handpicked selections from our curated collection</p>
            </div>
            <Button variant="ghost" onClick={() => navigate('/catalog')} data-testid="featured-view-all-button">
              View All <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-muted rounded-t-xl"></div>
                  <div className="p-4 space-y-3"><div className="h-4 bg-muted rounded"></div><div className="h-3 bg-muted rounded w-3/4"></div><div className="h-4 bg-muted rounded w-1/2"></div></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="featured-watches-grid">
              {featuredWatches.map((watch) => (
                <Card
                  key={watch.id}
                  className="group relative rounded-xl border border-border bg-card shadow-[0_2px_24px_rgba(0,0,0,0.35)] hover:shadow-[0_8px_40px_rgba(0,0,0,0.45)] transition-shadow cursor-pointer"
                  onClick={() => navigate(`/watch/${watch.id}`)}
                  data-testid="product-card"
                >
                  <div className="aspect-[4/5] overflow-hidden rounded-t-xl bg-black/40 relative">
                    <img
                      src={watch.images?.[0] || 'https://images.unsplash.com/photo-1589688934051-ca9059383f4f?w=800'}
                      alt={watch.title}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                    {watch.status === 'sold' && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <Badge className="bg-red-600 text-white border-0 text-sm px-4 py-1">Sold</Badge>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-display text-base text-foreground truncate" data-testid="product-title">
                      {watch.title}
                    </h3>
                    <p className="text-xs text-muted-foreground truncate" data-testid="product-subtitle">
                      {watch.refNumber}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-primary font-semibold" data-testid="product-price-text">
                        {formatPrice(watch.price, watch.currency)}
                      </span>
                      <Badge variant="outline" className="text-[10px] bg-transparent border-primary/30 text-foreground" data-testid="product-condition-badge">
                        {watch.condition}
                      </Badge>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Brand Marquee */}
      <section className="py-12 border-y border-border overflow-hidden" data-testid="brands-section">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px] mb-6">
          <p className="text-muted-foreground text-sm">Curated Selection</p>
          <h3 className="font-display text-2xl text-foreground font-semibold">Featured Brands</h3>
        </div>
        <div className="brand-marquee-wrapper">
          <div className="brand-marquee">
            {[...displayBrands, ...displayBrands].map((brand, i) => (
              <button
                key={`${brand}-${i}`}
                onClick={() => navigate(`/catalog?brand=${encodeURIComponent(brand)}`)}
                className="inline-flex items-center px-6 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
                data-testid={`brand-marquee-${brand.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {brand}
              </button>
            ))}
          </div>
        </div>
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px] mt-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/catalog?brand=')} className="text-primary">
            View more brands <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20" data-testid="trust-section">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px]">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl text-foreground tracking-tight font-semibold mb-4">The V watch Difference</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Where collectors trade with confidence. Every transaction protected, every timepiece authenticated.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 border-border bg-card" data-testid="trust-card-authentication">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4"><Shield className="h-6 w-6 text-primary" /></div>
              <h3 className="font-display text-xl text-foreground mb-2 font-semibold">Authentication Guarantee</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">Every watch undergoes rigorous authentication by certified experts before listing. Your purchase is guaranteed authentic.</p>
            </Card>
            <Card className="p-6 border-border bg-card" data-testid="trust-card-escrow">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4"><Crown className="h-6 w-6 text-primary" /></div>
              <h3 className="font-display text-xl text-foreground mb-2 font-semibold">Secure Transactions</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">All payments held in escrow until successful delivery. Buyer and seller protection on every transaction.</p>
            </Card>
            <Card className="p-6 border-border bg-card" data-testid="trust-card-shipping">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4"><Clock className="h-6 w-6 text-primary" /></div>
              <h3 className="font-display text-xl text-foreground mb-2 font-semibold">Insured Worldwide Shipping</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">Fully insured shipping to over 100 countries. Track your timepiece every step of the way.</p>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
